import {
  ACTIVITY_TYPES,
  MAX_WEEKLY_GOAL_MIN,
  MIN_WEEKLY_GOAL_MIN,
  WHO_WEEKLY_MINUTES,
  ageFromProfile,
} from '@movmania/core'
import { Button, Card, Figure, Screen, ScreenBody, SectionTitle } from '../components/Ui.js'
import { ActivityTileGrid } from '../components/ActivityTile.js'
import { useGame } from '../lib/useGame.js'
import { useT } from '../lib/useT.js'
import { useStore } from '../store/useStore.js'

export function Profile() {
  const t = useT()
  const game = useGame()
  const profile = useStore((s) => s.profile)
  const setWeeklyGoal = useStore((s) => s.setWeeklyGoal)
  const setFavorites = useStore((s) => s.setFavorites)
  const loadDemoHistory = useStore((s) => s.loadDemoHistory)
  const reset = useStore((s) => s.reset)

  if (!profile) return null
  const favorites = profile.favoriteActivities
  const age = ageFromProfile(profile)

  return (
    <Screen>
      <ScreenBody className="safe-top pb-8">
        <header className="mb-6 flex items-center gap-4">
          <span
            className="bg-green text-surface text-title grid size-16 shrink-0 place-items-center rounded-full font-bold"
            aria-hidden="true"
          >
            {profile.displayName.charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <h1 className="text-title truncate font-bold">{profile.displayName}</h1>
            <p className="text-caption text-ink-3">{t(`primaryGoal.${profile.primaryGoal}`)}</p>
          </div>
        </header>

        <Card className="divide-sunken grid grid-cols-3 divide-x">
          <Stat value={game.stats.totalSessions} label={t('profile.totalSessions')} />
          <Stat
            value={Math.round(game.stats.totalMinutes / 60)}
            unit="h"
            label={t('profile.totalHours')}
          />
          <Stat value={game.streak.best} label={t('profile.bestStreak')} />
        </Card>

        <section className="mt-8">
          <SectionTitle>{t('profile.weeklyGoal')}</SectionTitle>
          <Card>
            <div className="mb-2 flex items-baseline justify-between">
              <Figure value={game.weeklyGoalMin} unit="min" size="display" />
              <span className="text-caption text-ink-3 text-right">
                {t('onboarding.targetWho', { count: WHO_WEEKLY_MINUTES })}
              </span>
            </div>
            <input
              type="range"
              min={MIN_WEEKLY_GOAL_MIN}
              max={MAX_WEEKLY_GOAL_MIN}
              step={15}
              value={game.weeklyGoalMin}
              onChange={(event) => setWeeklyGoal(Number(event.target.value))}
              className="slider"
              aria-label={t('profile.weeklyGoal')}
            />
          </Card>
        </section>

        <section className="mt-8">
          <SectionTitle>{t('profile.favorites')}</SectionTitle>
          <ActivityTileGrid
            types={ACTIVITY_TYPES}
            isSelected={(type) => favorites.includes(type)}
            onToggle={(type) =>
              setFavorites(
                favorites.includes(type)
                  ? favorites.filter((item) => item !== type)
                  : [...favorites, type],
              )
            }
          />
        </section>

        {(age || profile.heightCm || profile.weightKg) && (
          <section className="mt-8">
            <SectionTitle>{t('profile.body')}</SectionTitle>
            <Card className="divide-sunken grid grid-cols-3 divide-x">
              <Stat
                value={age ?? '—'}
                unit={age ? t('onboarding.bodyYears') : ''}
                label={t('onboarding.bodyAge')}
              />
              <Stat
                value={profile.heightCm ?? '—'}
                unit={profile.heightCm ? 'cm' : ''}
                label={t('onboarding.bodyHeight')}
              />
              <Stat
                value={profile.weightKg ?? '—'}
                unit={profile.weightKg ? 'kg' : ''}
                label={t('onboarding.bodyWeight')}
              />
            </Card>
          </section>
        )}

        <section className="mt-8">
          <SectionTitle>Prototype</SectionTitle>
          <div className="flex flex-col gap-2">
            <Button variant="secondary" full onClick={loadDemoHistory}>
              {t('profile.loadDemo')}
            </Button>
            <Button
              variant="danger"
              full
              onClick={() => {
                if (confirm(t('profile.resetConfirm'))) reset()
              }}
            >
              {t('profile.reset')}
            </Button>
          </div>
        </section>

        <p className="text-caption text-ink-3 mt-6 text-center">{t('profile.disclaimer')}</p>
      </ScreenBody>
    </Screen>
  )
}

function Stat({
  value,
  unit,
  label,
}: {
  value: string | number
  unit?: string
  label: string
}) {
  return (
    <div className="px-2 text-center first:pl-0 last:pr-0">
      <Figure value={value} {...(unit ? { unit } : {})} />
      <p className="text-caption text-ink-3 mt-1">{label}</p>
    </div>
  )
}
